import { useEffect, useRef, useState } from "react";
import { MitelChatHandler } from "@/common/services/livechat/mitel/mitel-chat-handler.js";
import type { ChatMessage, ChatMessageUser, LiveAgentMessage, SystemMessage } from "../types/chat.types";

export function useMitelLivechat(
  mitelLiveChatData: any | null, 
  onNewMessage: (msg: ChatMessage) => void
) {
  const handlerRef = useRef<any>(null);

  const [agentName, setAgentName] = useState<string | null>(null);
  const [queue, setQueue] = useState<{ position: number, waitTime: number } | null>(null);
  const [chatState, setChatState] = useState<"queued"|"handling"|"terminated"|"idle">("idle");
  const [isConnecting, setIsConnecting] = useState(false);
  const [isAgentTyping, setIsAgentTyping] = useState(false);
  const queuePositionRef = useRef<number | null>(null);

  useEffect(() => {
    if (!mitelLiveChatData) {
      setAgentName(null);
      setQueue(null);
      setChatState("idle");
      setIsConnecting(false);
      queuePositionRef.current = null;
      
      return;
    }

    console.info("Initiating Mitel");

    if (handlerRef.current) {
      handlerRef.current.disconnect?.();
      handlerRef.current = null;
    }
    handlerRef.current = new MitelChatHandler(mitelLiveChatData);

    // Register listeners
    handlerRef.current.on("chatState", (info: any) => {
      if (info.QueuePosition !== undefined) {
        setQueue({ position: info.QueuePosition, waitTime: info.EstimatedWaitTime ?? 0 });
        
        if (info.QueuePosition > 0 &&
            queuePositionRef.current !== info.QueuePosition &&
            info.State !== 1) {
          const message: SystemMessage = {
            sender: "system",
            content: `Du har plats ${info.QueuePosition} i kön.`,
            type: "system"
          };
          onNewMessage(message);
          queuePositionRef.current = info.QueuePosition;
        }
      }

      if (info.State === 0) {
        setChatState('queued');
      } else if (info.State === 1) {
        setChatState('handling');
      } else if (info.State === 2) {
        setChatState('terminated');

        let terminationMessage = "Chatten har avslutats.";
        if (info.TerminationCause === 1) {
          terminationMessage = "Livechatten är stängd just nu. Vänligen försök igen under våra öppettider.";
        }
        
        const message: SystemMessage = {
          sender: "system",
          content: terminationMessage,
          type: "system"
        };
        onNewMessage(message);
      }
    });
    handlerRef.current.on("agentJoined", (data: any) => {
      const agentName = data?.Name || "Agent";
      setAgentName(agentName);
      const message: SystemMessage = {
        sender: "system",
        content: `${agentName} har anslutit till chatten.`,
        type: "system"
      };
      onNewMessage(message);
    });
    handlerRef.current.on("agentLeft", (data: any) => {
      const message: SystemMessage = {
        sender: "system",
        content: `${data?.Name || "Agent"} har lämnat chatten. Tack för att du chattade med oss.`,
        type: "system"
      };
      onNewMessage(message);
      setChatState("terminated");
      handlerRef.current?.disconnect?.();
    });
    handlerRef.current.on("agentMessageReceived", (msg: any) => {
      const agentMsg: LiveAgentMessage = {
        sender: msg.Name || agentName || "Agent",
        content: msg.Message,
        timestamp: parseTimeStamp(msg.TimeStamp),
        attachment: msg.Attachment,
        type: "agent"
      };
      setIsAgentTyping(false);
      onNewMessage(agentMsg);
    });
    handlerRef.current.on("pollingImpossible", (err: any) => {
      console.error("[useMitelLivechat] pollingImpossible:", err);
      const message: SystemMessage = {
        sender: "system",
        content: "Ett tekniskt fel uppstod.",
        type: "system"
      };
      onNewMessage(message);
    });
    handlerRef.current.on("agentTyping", () => {
      setIsAgentTyping(true);
    });

    const isConnected = handlerRef.current.isConnected();
    console.log('[useMitelLivechat] Handler connection status:', isConnected ? 'Connected' : 'Not connected');
    
    if (isConnected) {
      console.log('[useMitelLivechat] Already connected, starting event polling');
      handlerRef.current.startEventPolling();
    } else {
      console.log('[useMitelLivechat] Connecting to Mitel...');
      setIsConnecting(true);
      handlerRef.current.connect()
        .then(() => {
          console.log('[useMitelLivechat] Connected successfully!');
          setIsConnecting(false);
          if (handlerRef.current) {
            handlerRef.current.startEventPolling();
          }
        })
        .catch((err: any) => {
          console.error('[useMitelLivechat] Connection failed:', err);
          setIsConnecting(false);
        });
    }

    return () => {
      handlerRef.current?.disconnect();
    };
  }, [mitelLiveChatData]);

  const sendMessage = async (text: string) => {
    if (!handlerRef.current || !handlerRef.current.isConnected()) {
      console.warn("Livechat not ready, cannot send message yet");
      return;
    }
    const message: ChatMessageUser = {
      sender: "Du",
      content: text,
      type: "user"
    };
    onNewMessage(message);
    await handlerRef.current.sendMessage(text);
  };

  const sendSummary = async (text: string) => {
    if (!handlerRef.current || !handlerRef.current.isConnected()) {
      console.warn("Livechat not ready, cannot send message yet");
      return;
    }
    await handlerRef.current.sendMessage(text);
  };

  const disconnect = () => {
    if (handlerRef.current) {
      console.log('[useMitelLivechat] Disconnecting handler');
      handlerRef.current.disconnect?.();
      handlerRef.current = null;
    }
  };

  const isConnected = () => {
    return handlerRef.current?.isConnected() ?? false;
  };

  const parseTimeStamp = (ts: string | undefined): string | undefined => {
    if (!ts || typeof ts !== "string") return undefined;
    
    const match = /\/Date\((\d+)\)\//.exec(ts);
    if (!match) return undefined;
    
    const timestamp = Number(match[1]);
    const date = new Date(timestamp);
    
    return isNaN(date.getTime()) ? undefined : date.toISOString();
  };


  return {
    chatState,
    agentName,
    queue,
    sendMessage,
    disconnect,
    sendSummary,
    isConnected,
    isConnecting,
    isAgentTyping
  };
}